import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { selectMultiScreen } from '@src/store/actionSlices/multiScreen'

import { ProjectIdentity } from '@store/types'

import { Modal } from '@components/modals'

import { generateId } from '@utilities/helper'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import { ArrowRefreshSvg, FrameRefreshSvg } from '@svg/react'

interface FirebaseRefreshTriggerParams {
  state: 'registered' | 'cleared'
  sessionKey?: string
  key?: string
}

interface RefreshSessionHandlerProps {
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
}

function RefreshSessionHandler({
  projectIdentity,
  session,
}: RefreshSessionHandlerProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const history = useHistory()

  const multiScreen = useSelector(selectMultiScreen)

  const [isModalActive, modalToggle] = useState(false)
  const [refreshTriggerKeyRestTimerId, setRefreshTriggerKeyRestTimerId] =
    useState<ReturnType<typeof setTimeout>>()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const setRefreshTriggerKeyOnFirebase = async ({
    state,
    sessionKey,
    key = '',
  }: FirebaseRefreshTriggerParams) => {
    await firebaseControlQuery
      .update(
        {
          [`clearCacheTriggerKey`]: key,
          [`clearCacheKeyState`]: state,
        },
        sessionKey
      )
      .then(async () => {
        if (!session) {
          return
        }

        await firebaseControlQuery.update(
          {
            [`activeRoute`]: 'vision',
          },
          sessionKey
        )
      })
  }

  const handleRefreshClick = async () => {
    setIsRefreshing(true)

    const triggerKey = generateId(10)
    const isMultiScreenActive = multiScreen.active

    if (isMultiScreenActive) {
      const activeProjectSessionKeys = multiScreen.displayScreens.flatMap(
        (screen) =>
          screen.sessionKeys
            .filter((sess) => sess.projectId === projectIdentity.projectName)
            .map((sess) => sess.key)
      )

      await Promise.allSettled(
        activeProjectSessionKeys.map((sessionKey) =>
          setRefreshTriggerKeyOnFirebase({
            state: 'registered',
            sessionKey,
            key: triggerKey,
          })
        )
      )
    }

    await setRefreshTriggerKeyOnFirebase({
      state: 'registered',
      key: triggerKey,
    })
  }

  const resetRefreshTriggerKeyTimer = () => {
    setIsRefreshing(false)
    if (refreshTriggerKeyRestTimerId) {
      clearTimeout(refreshTriggerKeyRestTimerId)
      setRefreshTriggerKeyRestTimerId(undefined)
    }
  }

  const resetRefreshKeyStateAndReload = async (state?: string) => {
    if (state !== 'cleared') return

    await firebaseControlQuery
      .update({
        [`clearCacheKeyState`]: '',
        [`activeRoute`]: 'vision',
      })
      .then(() => {
        modalToggle(false)
        history.push('vision')
        window.location.reload()
      })
  }

  const handleRefreshTriggerKeyChange = async (
    refreshTriggerKey?: string,
    myRefreshKeyState?: string
  ) => {
    if (!refreshTriggerKey) {
      resetRefreshTriggerKeyTimer()
      resetRefreshKeyStateAndReload(myRefreshKeyState)
      return
    }

    if (!refreshTriggerKeyRestTimerId) {
      setRefreshTriggerKeyRestTimerId(
        setTimeout(() => {
          setRefreshTriggerKeyOnFirebase({
            state: 'cleared',
            key: '',
          })
        }, 30 * 1000)
      )
    }
  }

  useEffect(() => {
    if (!session) {
      return
    }

    const {
      clearCacheTriggerKey: firebaseRefreshTriggerKey,
      clearCacheKeyState: firebaseRefreshKeyState,
    } = session

    handleRefreshTriggerKeyChange(
      firebaseRefreshTriggerKey,
      firebaseRefreshKeyState
    )
  }, [session])

  return (
    <>
      <Modal
        toggleModal={(res) => modalToggle(res)}
        handleApply={handleRefreshClick}
        isVisible={isModalActive}
        title="Refresh session"
        applyButtonText="Cancel"
        theme={{
          textColor: 'text-mainColour',
          mainBg: 'bg-mainColour',
          secondaryBg: 'bg-neutralColour',
        }}
        noMainControls
      >
        <div className="p-5 text-default text-mainColour">
          Click below to Refresh your Showcase remote session.
        </div>
        <div className="flex items-center justify-between gap-4 border-gray-200 pt-3">
          <button
            type="button"
            disabled={isRefreshing}
            onClick={() => modalToggle(false)}
            className="w-full rounded-lg border bg-neutralColour py-5 text-subHeading font-normal text-mainColour"
          >
            Close
          </button>
          <button
            disabled={isRefreshing}
            type="button"
            onClick={handleRefreshClick}
            className="flex w-full items-center justify-center rounded-lg bg-gradient py-5 text-subHeading font-normal text-white"
          >
            Refresh
            {isRefreshing && (
              <ArrowRefreshSvg
                className="ml-2 animate-spin text-white"
                size="lg"
              />
            )}
          </button>
        </div>
      </Modal>
      <button
        onClick={() => modalToggle(true)}
        type="button"
        className="ml-auto flex items-center text-default text-neutralColour"
      >
        Refresh Session
        <FrameRefreshSvg className="ml-3 text-neutralColour" size="lg" />
      </button>
    </>
  )
}

export default RefreshSessionHandler
